/**
 * Shared User Data Processing Utilities
 * Common logic for processing user data across multiple analytics functions
 * 
 * Note: validateUserData and isTestUser are passed as parameters to avoid circular dependencies
 */

// These will be passed as parameters to avoid circular imports
// import { validateUserData, isTestUser } from './firebaseService';

/**
 * Process user data and calculate common metrics
 * @param {Array} usersSnapshot - Firestore users snapshot docs array
 * @param {Array} splitsSnapshot - Firestore splits snapshot docs array
 * @param {Array} transactionsSnapshot - Firestore transactions snapshot docs array
 * @param {Function} validateUserData - Function to validate user data
 * @param {Function} isTestUser - Function to check if user is a test user
 * @returns {Object} - Processed user data with activity maps
 */
export const processUserDataForAnalytics = (usersSnapshot, splitsSnapshot, transactionsSnapshot, validateUserData, isTestUser) => {
  const now = new Date();
  const userActivityMap = new Map();
  const userSplitCountMap = new Map();
  const userTransactionCountMap = new Map();
  const userValueMap = new Map();
  const userBadgeCountMap = new Map();
  const firstSplitMap = new Map();
  const firstTransactionMap = new Map();
  const allUsers = [];

  // Process users
  usersSnapshot.forEach(doc => {
    const rawUserData = doc.data();
    
    // Exclude test users
    if (isTestUser(rawUserData)) {
      return;
    }
    
    const userData = validateUserData(rawUserData);
    const userId = doc.id;
    
    // Calculate days since signup
    const signupDate = userData.createdAt ? new Date(userData.createdAt) : null;
    const daysSinceSignup = signupDate ? Math.floor((now - signupDate) / (1000 * 60 * 60 * 24)) : null;
    
    // Calculate days since last login
    const lastLoginDate = userData.lastLoginAt ? new Date(userData.lastLoginAt) : null;
    const daysSinceLastLogin = lastLoginDate ? Math.floor((now - lastLoginDate) / (1000 * 60 * 60 * 24)) : null;
    
    // Count badges
    const badgeCount = Array.isArray(rawUserData.badges) ? rawUserData.badges.length : 0;
    userBadgeCountMap.set(userId, badgeCount);
    
    // Initialize user activity
    const userActivity = {
      userId,
      email: userData.email,
      name: userData.name,
      signupDate,
      daysSinceSignup,
      lastLoginDate,
      daysSinceLastLogin,
      splitCount: 0,
      transactionCount: 0,
      transactionValue: 0,
      badgeCount,
      hasWallet: !!userData.walletAddress,
      isVerified: userData.emailVerified,
      isOnboarded: userData.hasCompletedOnboarding
    };
    
    userActivityMap.set(userId, userActivity);
    allUsers.push(userActivity);
  });
  
  // Process splits - count per user and find first split date
  splitsSnapshot.forEach(doc => {
    const splitData = doc.data();
    const userId = splitData.creatorId || splitData.userId || splitData.creator_id || splitData.user_id;
    
    if (userId && userActivityMap.has(userId)) {
      const user = userActivityMap.get(userId);
      user.splitCount++;
      userSplitCountMap.set(userId, (userSplitCountMap.get(userId) || 0) + 1);
      
      // Track first split date
      const createdAt = splitData.createdAt || splitData.created_at || splitData.timestamp;
      if (createdAt) {
        const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        if (!firstSplitMap.has(userId) || date < firstSplitMap.get(userId)) {
          firstSplitMap.set(userId, date);
        }
      }
    }
  });
  
  // Process transactions - count per user, calculate value, and find first transaction date
  transactionsSnapshot.forEach(doc => {
    const txData = doc.data();
    const userId = txData.userId || txData.user_id || txData.creatorId || txData.creator_id;
    
    if (userId && userActivityMap.has(userId)) {
      const user = userActivityMap.get(userId);
      user.transactionCount++;
      const amount = parseFloat(txData.amount || 0);
      user.transactionValue += amount;
      userTransactionCountMap.set(userId, (userTransactionCountMap.get(userId) || 0) + 1);
      userValueMap.set(userId, (userValueMap.get(userId) || 0) + amount);
      
      // Track first transaction date
      const createdAt = txData.createdAt || txData.created_at || txData.timestamp;
      if (createdAt) {
        const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        if (!firstTransactionMap.has(userId) || date < firstTransactionMap.get(userId)) {
          firstTransactionMap.set(userId, date);
        }
      }
    }
  });
  
  // Calculate first activity date for each user
  allUsers.forEach(user => {
    const firstSplitDate = firstSplitMap.get(user.userId);
    const firstTransactionDate = firstTransactionMap.get(user.userId);
    user.firstActivityDate = firstSplitDate && firstTransactionDate
      ? (firstSplitDate < firstTransactionDate ? firstSplitDate : firstTransactionDate)
      : (firstSplitDate || firstTransactionDate);
    
    // Calculate time to value (days from signup to first activity)
    if (user.signupDate && user.firstActivityDate) {
      user.timeToValue = Math.floor((user.firstActivityDate - user.signupDate) / (1000 * 60 * 60 * 24));
    } else {
      user.timeToValue = null;
    }
  });
  
  return {
    userActivityMap,
    allUsers,
    userSplitCountMap,
    userTransactionCountMap,
    userValueMap,
    userBadgeCountMap,
    firstSplitMap,
    firstTransactionMap
  };
};

/**
 * Segment users by engagement level
 * @param {Array} allUsers - Array of processed user objects
 * @returns {Object} - Segmented users by engagement
 */
export const segmentUsersByEngagement = (allUsers) => {
  return {
    powerUsers: allUsers.filter(user => user.daysSinceLastLogin !== null && user.daysSinceLastLogin <= 1),
    activeUsers: allUsers.filter(user => user.daysSinceLastLogin !== null && user.daysSinceLastLogin > 1 && user.daysSinceLastLogin <= 7),
    casualUsers: allUsers.filter(user => user.daysSinceLastLogin !== null && user.daysSinceLastLogin > 7 && user.daysSinceLastLogin <= 30),
    dormantUsers: allUsers.filter(user => user.daysSinceLastLogin === null || user.daysSinceLastLogin > 30)
  };
};

/**
 * Segment users by lifecycle stage
 * @param {Array} allUsers - Array of processed user objects
 * @returns {Object} - Segmented users by lifecycle
 */
export const segmentUsersByLifecycle = (allUsers) => {
  return {
    newUsers: allUsers.filter(user => user.daysSinceSignup !== null && user.daysSinceSignup <= 7),
    activeUsers: allUsers.filter(user => user.daysSinceLastLogin !== null && user.daysSinceLastLogin <= 7),
    atRiskUsers: allUsers.filter(user => user.daysSinceLastLogin !== null && user.daysSinceLastLogin > 7 && user.daysSinceLastLogin <= 30),
    churnedUsers: allUsers.filter(user => user.daysSinceLastLogin === null || user.daysSinceLastLogin > 30)
  };
};

/**
 * Segment users by feature usage
 * @param {Array} allUsers - Array of processed user objects
 * @returns {Object} - Segmented users by feature usage
 */
export const segmentUsersByFeatureUsage = (allUsers) => {
  return {
    splitCreators: allUsers.filter(user => user.splitCount > 0),
    badgeEarners: allUsers.filter(user => user.badgeCount > 0),
    walletUsers: allUsers.filter(user => user.hasWallet),
    sharedWalletUsers: [] // Would need shared wallet data
  };
};

/**
 * Segment users by value tier
 * @param {Array} allUsers - Array of processed user objects
 * @returns {Object} - Segmented users by value
 */
export const segmentUsersByValue = (allUsers) => {
  const sortedByValue = [...allUsers].sort((a, b) => b.transactionValue - a.transactionValue);
  const highValueThreshold = Math.floor(sortedByValue.length * 0.2);
  const lowValueThreshold = Math.floor(sortedByValue.length * 0.8);
  
  return {
    highValue: sortedByValue.slice(0, highValueThreshold),
    mediumValue: sortedByValue.slice(highValueThreshold, lowValueThreshold),
    lowValue: sortedByValue.slice(lowValueThreshold)
  };
};

